import React, { useMemo } from 'react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { Box, Button, Flex, Menu, MenuButton, MenuItem, MenuList, Text, VStack } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import { Appliance } from '../../../../../api/api-appliance';
import { selectBatteries, selectEVChargers } from '../../../system-details/systemDetailsSlice';
import { useMeterSwitches } from '../../switch-configuration/switchApi';
import { selectAllAppliances, updateAppliance } from '../circuitApplianceConfigSlice';

type ControlDeviceSelectProps = {
  appliance: Appliance;
};

export const ControlDeviceSelect = ({ appliance }: ControlDeviceSelectProps) => {
  const batteries = useSelector(selectBatteries);
  const evChargers = useSelector(selectEVChargers);
  const allAppliances = useSelector(selectAllAppliances);
  const selectedControlDevices = useMemo(
    () => allAppliances.map((a) => ({ id: a.control_device_id, name: a.appliance_name })).filter((a) => a.id !== null),
    [allAppliances]
  );
  const { switches, isLoading } = useMeterSwitches();
  const dispatch = useDispatch();

  const selectOptions = useMemo(() => {
    const options = [];

    const batterySelectOptions = batteries.map((battery) => ({
      controlDeviceId: battery.row_id || 0,
      type: 'Battery',
      serialNumber: battery.serial_number,
      siteIdentifier: battery.site_identifier,
      label: '',
    }));

    const evChargerSelectOptions = evChargers.map((evCharger) => ({
      controlDeviceId: evCharger.row_id || 0,
      type: 'EV Charger',
      serialNumber: evCharger.serial_number,
      siteIdentifier: evCharger.site_identifier,
      label: '',
    }));

    const switchSelectOptions = switches
      .filter((sw) => sw.active) // allow only active switches
      .map((sw) => ({
        controlDeviceId: sw.id,
        type: 'Switch',
        serialNumber: null,
        siteIdentifier: null,
        label: sw.site_switch_label,
      }));

    // Add all eligible devices to the list
    options.push(...batterySelectOptions, ...evChargerSelectOptions, ...switchSelectOptions);

    return options;
  }, [batteries, evChargers, switches]);

  const selectedOptionLabel = useMemo(() => {
    const selectedOption = selectOptions.find((option) => option.controlDeviceId === appliance.control_device_id);

    if (!selectedOption) return 'Select Control Device';

    // labels can have empty attributes, so we need to prioritize
    return `${selectedOption.type} (${
      selectedOption.label ||
      selectedOption.serialNumber ||
      selectedOption.siteIdentifier ||
      selectedOption.controlDeviceId
    })`;
  }, [selectOptions, appliance]);

  const handleUpdateControlDevice = (controlDeviceId: number | null) => {
    dispatch(updateAppliance({ applianceId: appliance.appliance_id, controlDeviceId }));
  };

  return (
    <Menu>
      <MenuButton
        data-testid={`select-control-device-appliance-${appliance.appliance_id}`}
        as={Button}
        rightIcon={<ChevronDownIcon w={5} h={5} />}
        w="100%"
        textAlign={'left'}
        variant={'outline'}
        isDisabled={!selectOptions.length || isLoading}
        px={1}
        color={'gray.600'}
        _dark={{ color: 'gray.400' }}
      >
        <Flex>
          <Text
            ml={2}
            isTruncated
            fontWeight={'normal'}
            data-testid={`selected-control-device-label-${appliance.appliance_id}`}
          >
            {selectedOptionLabel}
          </Text>
        </Flex>
      </MenuButton>
      <MenuList
        data-testid={`control-device-select-${appliance.appliance_id}`}
        zIndex={999999999999}
        maxW={'calc(100vw - 32px)'}
        mx={4}
      >
        <MenuItem
          key={'switch-categoryLabel-option-none'}
          data-testid={'switch-appliance-association-option-none'}
          bg={appliance.control_device_id === null ? 'customGreen.100' : undefined}
          _dark={{ bg: appliance.control_device_id === null ? 'customGreen.600' : undefined }}
          onClick={() => handleUpdateControlDevice(null)}
          p={0}
        >
          <Box borderBottom={'1px solid'} borderColor={'gray.400'} p={4} w="100%">
            <VStack align={'flex-start'}>
              <Text>No Control Device Associated</Text>
            </VStack>
          </Box>
        </MenuItem>
        {selectOptions.map(({ label, controlDeviceId, type, serialNumber, siteIdentifier }, i) => {
          const controlDevice = selectedControlDevices.find((d) => d.id === controlDeviceId);
          const isSelected = controlDeviceId === appliance.control_device_id;
          const isReserved = !!controlDevice && !isSelected;
          const isLastItem = i === selectOptions.length - 1;
          return (
            <MenuItem
              isDisabled={isReserved}
              key={`switch-categoryLabel-option-${i}`}
              data-testid={`switch-appliance-association-option-${i}`}
              bg={isSelected ? 'customGreen.100' : undefined}
              _dark={{ bg: isSelected ? 'customGreen.600' : undefined }}
              onClick={() => handleUpdateControlDevice(controlDeviceId || null)}
              p={0}
            >
              <Box borderBottom={isLastItem ? 'none' : '1px solid'} borderColor={'gray.400'} p={4} w="100%">
                <VStack align={'flex-start'}>
                  <Text>
                    {type} ({label || serialNumber || controlDeviceId})
                  </Text>
                  {serialNumber && <Text>Serial Number: {serialNumber}</Text>}
                  {siteIdentifier && <Text>Site Identifier: {siteIdentifier}</Text>}
                  {isReserved && <Text>(Linked to appliance - {controlDevice.name})</Text>}
                </VStack>
              </Box>
            </MenuItem>
          );
        })}
      </MenuList>
    </Menu>
  );
};
